import { PaymentIntent } from '@stripe/stripe-js';
import { useStripe } from '@stripe/react-stripe-js';
import { FC, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import { getPayStripePaymentIntent } from 'selectors/pay';
import { getUserData } from 'selectors/user';
import { IEvent } from 'store/event/types';
import { IPage } from 'store/pages/types';
import theme from 'config/theme';
import classNames from 'classnames';
import { canShowSosha } from '../utils';
import { useNavigate, useParams } from 'react-router-dom';
import { ProfileCard } from 'components/Cards/ProfileCard';
import { Image } from 'components/Image';
import { Sosha } from '../shared/Sharing/Sosha';
import { formatNumber, formatNumberAsCurrency, scrollToTop } from 'helpers/utils';
import { PopupModal } from 'components/PopupModal';
import { ENV_CONFIG } from 'config/environment';
import { CTA } from 'components/ButtonBrand';
import { clearCheckoutIdempotencyKey } from 'services/storage';
import { useShareUrl } from 'hooks/useShareUrl';
import Loader from 'components/Loader';
import { trackPixel } from 'helpers/pixel';
import { storeStripePaymentIntent } from 'store/pay/actions';
import { Link } from 'components/Link';
const HIDE_SOSHA = true;
const IS_MONTHLY = false;
const getIsDonationSuccessful = (paymentIntent: PaymentIntent) =>
  paymentIntent.status === 'succeeded';
const getIsDonationPending = (paymentIntent: PaymentIntent) =>
  paymentIntent.status === 'processing';
const getDate = (paymentIntent: PaymentIntent) => {
  const dateObj = paymentIntent ? new Date(paymentIntent.created * 1000) : new Date();
  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};
const getTipAmount = (totalDonatedCents: number, totalSelectedDollars: number) =>
  Math.round(totalDonatedCents - totalSelectedDollars * 100);

interface Props {
  donationAmount: number;
  numRecipients: number; // replace with payment intent metadata when ready
  page?: IPage | IEvent;
}

export const StripeConfirmation: FC<Props> = ({ donationAmount, numRecipients, page }) => {
  const stripe = useStripe();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { urlPartner } = useParams();
  const { paymentIntent, user } = useAppSelector(state => ({
    paymentIntent: getPayStripePaymentIntent(state),
    user: getUserData(state),
  }));
  const { handleShare, isPopupOpen } = useShareUrl(user?.ref_code);

  useEffect(() => {
    if (!stripe || paymentIntent) {
      return;
    }
    // Retrieve the "payment_intent_client_secret" query parameter appended to
    // your return_url by Stripe.js
    const clientSecret = new URLSearchParams(window.location.search).get(
      'payment_intent_client_secret'
    );
    if (!clientSecret) {
      navigate(`/donate?p=${urlPartner}`);
      return;
    }
    // Retrieve the PaymentIntent
    stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
      if (!paymentIntent) {
        return;
      }
      dispatch(storeStripePaymentIntent(paymentIntent));
    });
  }, [stripe]);

  useEffect(() => {
    clearCheckoutIdempotencyKey();
    scrollToTop();
    trackPixel('purchase');
    trackPixel('universalPixel');
  }, []);

  if (!paymentIntent) {
    return (
      <div className="confirmation">
        <div className="loader-container">
          <Loader width={60} height={60} />
        </div>
      </div>
    );
  }

  const tip = donationAmount ? getTipAmount(paymentIntent.amount, donationAmount) : null; // need to add tip to payment intent metaData
  const donorFullName = user ? `${user.first_name} ${user.last_name}` : '';
  const isEvent = !!page?.event_id;
  const textColor = isEvent ? 'white' : 'inkBlue';
  const headerBackground = isEvent ? theme.colors.oathBlue : undefined;
  const isDonationPending = getIsDonationPending(paymentIntent);
  const isDonationSuccessful = getIsDonationSuccessful(paymentIntent);
  const date = getDate(paymentIntent);
  const pageImage =
    urlPartner && page
      ? `${ENV_CONFIG().CLOUD_STORAGE.URL}/${ENV_CONFIG().CLOUD_STORAGE.TAGS}/tag_${page.id}.png`
      : '';

  return (
    <div className="confirmation">
      <div className={classNames('confirmation-background-container', { isEvent: 'bg-ink-blue' })}>
        <div className="confirmation-container">
          <div className="confirmation-container-left">
            <span className={`eyebrow-text mb5 color-${textColor}`}>
              Donation
              {isEvent ? ' / Ticket Purchase' : ''}
              {isDonationPending ? ' pending' : isDonationSuccessful ? ' complete' : ' failed'}
            </span>
            <span className={`h4-text mb5 color-${textColor}`}>
              {isDonationPending
                ? 'Your donation is being processed'
                : isDonationSuccessful
                ? 'Thank you for your donation!'
                : 'Your donation could not be processed'}
            </span>
            <span className={`body-text color-${textColor}`}>
              {isDonationPending
                ? 'We are currently processing your payment'
                : isDonationSuccessful
                ? (page as IEvent)?.post_donate_msg ||
                  'Keep the momentum going and invite your friends and family to donate.'
                : 'We could not process your payment.'}
            </span>
            {pageImage && (
              <Image className="confirmation-image" alt="Donate page logo" src={pageImage} />
            )}
            {canShowSosha(isDonationPending, urlPartner) && !HIDE_SOSHA ? (
              <Sosha urlPartner={urlPartner} />
            ) : isDonationSuccessful || isDonationPending ? (
              <div className="confirmation-button-row">
                <CTA className="button button-brand" onClick={handleShare}>
                  Share link
                </CTA>
              </div>
            ) : null}
          </div>
          {isDonationPending ? (
            <div className="confirmation-card-container">
              <ProfileCard
                title="Pending Authorization Status"
                align="center"
                smallTitle={true}
                headerBackground={headerBackground}
              >
                <div className="processing-card-content">
                  <Image
                    className="processing-image"
                    src={`${
                      ENV_CONFIG().CLOUD_STORAGE.URL
                    }/static_content/misc_icons/circle_arrows.png`}
                    alt="Circle formed by two arrows"
                  />
                  <span className="body-text">We are currently processing your payment.</span>
                  <br />
                  <span className="body-text">
                    Once received, we will send you an email confirming your donation.
                  </span>
                </div>
              </ProfileCard>
            </div>
          ) : isDonationSuccessful ? (
            <div className="confirmation-container-right">
              <div className="confirmation-card-container mb14">
                <ProfileCard title="Thank you!" align="center" headerBackground={headerBackground}>
                  <div className="confirmation-card-content">
                    <span className="body-text mb4">Contribution Summary</span>
                    <div className="display-flex mb13" style={{ justifyContent: 'space-between' }}>
                      <div className="display-grid g3">
                        <span className="h4-text mb2">
                          {formatNumberAsCurrency(paymentIntent.amount / 100)}
                        </span>
                        <span className="utility-text">
                          {IS_MONTHLY ? 'Recurring monthly contribution' : 'One-time contribution'}
                        </span>
                        <span className="utility-text">
                          Including {tip !== null && `${formatNumberAsCurrency(tip / 100)} `}tip to
                          Oath
                        </span>
                      </div>
                      <div className="display-grid">
                        <span className="h4-text">
                          {formatNumber(numRecipients)} recipient
                          {numRecipients !== 1 && 's'}
                        </span>
                      </div>
                    </div>
                    <div className="display-grid g3">
                      <span className="body-text">{date}</span>
                      <span className="body-text">{donorFullName}</span>
                      <span className="body-text">{paymentIntent.receipt_email}</span>
                    </div>
                  </div>
                </ProfileCard>
              </div>
              <span className="body-text">We’ve emailed you a receipt for your records.</span>
            </div>
          ) : (
            <div className="confirmation-card-container">
              <ProfileCard title="Payment Error" align="center" headerBackground={headerBackground}>
                <div className="error-card-content">
                  <Image
                    className="error-image"
                    src={`${ENV_CONFIG().CLOUD_STORAGE.URL}/static_content/misc_icons/circle_x.png`}
                    alt="Circle with an x in the center"
                  />
                  <span className="body-text">There was an error processing your donation.</span>
                  <br />
                  <span className="body-text mb20">
                    Please{' '}
                    <Link to={`/donate?p=${urlPartner}`}>
                      <span className="body-text">try again</span>
                    </Link>{' '}
                    or use a different payment method.
                  </span>
                  <CTA className="button button-brand" to={`/donate?p=${urlPartner}`}>
                    Try Again
                  </CTA>
                </div>
              </ProfileCard>
            </div>
          )}
          <PopupModal autoClose={true} isSelected={isPopupOpen} title="Copied to Clipboard" />
        </div>
      </div>
    </div>
  );
};
